import { Button, Icon, IconButton, Tag } from '@pypestream/design-system';
import { FC, useState } from 'react';

import createWaves from '../assets/home/create-card-waves.jpg';
import pypeMark from '../assets/home/pype-mark.svg?url';
import voiceMark from '../assets/home/voice-mark.svg?url';
import { type SolutionCardFacts, updatedLabel } from './home-solution-facts';
import { StepsDemo, TalkDemo } from './home-start-demos';

import './home-start.scss';
import './home-start-demos.scss';

/** A soft shade at the Create card's foot, so its white text reads on the waves. */
const CREATE_SHADE =
  'linear-gradient(180deg, rgb(30 24 84 / 0%) 35%, rgb(30 24 84 / 38%) 100%)';

/**
 * Project Bobcat — a project's front door, from the Sep 29 prototype round
 * (`prostudio-build-flow-layouts.html`: `#homeLanding`, `#startPick`),
 * behind `bobcat-conversational-create`:
 *
 * - `HomeLanding`: a bento grid — Create as the big card, every agentic
 *   solution as a card beside it.
 * - `StartPick`: "How do you want to start?" — talk it through
 *   (`talk/talk-it-through.tsx`) or set it up step by step
 *   (`home-step-by-step.tsx`); both start with a Supervisor and a
 *   Knowledge Agent, and each card's "Preview" plays a demo of it.
 */

export interface LandingSolution {
  id: string;
  name: string;
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

const ENV_TAG: Record<
  'live' | 'sandbox' | 'draft',
  { label: string; variant: string }
> = {
  live: { label: 'Live', variant: 'success' },
  sandbox: { label: 'Sandbox', variant: 'warning' },
  draft: { label: 'Draft', variant: 'default' },
};

/** One agentic solution: where it runs, what it runs on, how big. */
const SolutionCard: FC<{
  solution: LandingSolution;
  facts?: SolutionCardFacts;
  onOpen: () => void;
}> = ({ solution, facts, onOpen }) => {
  const envs = facts?.envs.length ? facts.envs : (['draft'] as const);
  const updated = updatedLabel(facts?.updatedAt);
  return (
    <button
      type="button"
      className="c-home-landing__solution"
      data-testid="bobcat-home-solution-card"
      onClick={onOpen}
    >
      <span className="c-home-landing__solution-top">
        <span className="c-home-landing__solution-name">{solution.name}</span>
        {facts && (
          <span className="c-home-landing__envs">
            {envs.map((e) => (
              <Tag key={e} size="small" variant={ENV_TAG[e].variant as never}>
                {ENV_TAG[e].label}
              </Tag>
            ))}
          </span>
        )}
      </span>
      {facts && (
        <span className="c-home-landing__meta">
          {facts.channels.pype > 0 && (
            <span>
              <img src={pypeMark} width={16} height={16} alt="" />
              Pype
            </span>
          )}
          {facts.channels.voice > 0 && (
            <span>
              <img src={voiceMark} width={16} height={16} alt="" />
              Voice
            </span>
          )}
          {facts.agents !== undefined && (
            <span>
              <Icon name="microagent" size="xsmall" />
              {plural(facts.agents, 'agent')}
            </span>
          )}
        </span>
      )}
      {updated && <span className="c-home-landing__foot">{updated}</span>}
    </button>
  );
};

/**
 * A project with agentic solutions (prototype `#homeLanding`, `renderHome`):
 * one bento grid. Create is the big card, two rows tall — never a small
 * button — and every solution fills in beside it as a card.
 */
export const HomeLanding: FC<{
  firstName?: string;
  projectName?: string;
  solutions: LandingSolution[];
  facts: Map<string, SolutionCardFacts>;
  createDisabled?: boolean;
  onCreate: () => void;
  onOpen: (solution: LandingSolution) => void;
}> = ({
  firstName,
  projectName,
  solutions,
  facts,
  createDisabled,
  onCreate,
  onOpen,
}) => {
  // `null` while the search is closed; the text once it's open.
  const [search, setSearch] = useState<string | null>(null);
  const needle = (search ?? '').trim().toLowerCase();
  const shown = needle
    ? solutions.filter((s) => s.name.toLowerCase().includes(needle))
    : solutions;

  return (
    <div
      className="c-home-start c-home-start--wide"
      data-testid="bobcat-home-landing"
    >
      <header className="c-home-start__head c-home-landing__head">
        <div className="c-home-landing__head-text">
          <h1 className="c-home-start__title">
            Welcome back{firstName ? `, ${firstName}` : ''}
          </h1>
          <p className="c-home-start__sub">
            {plural(solutions.length, 'agentic solution')}
            {projectName ? ` in ${projectName}` : ''}.
          </p>
        </div>
        {solutions.length > 0 &&
          (search === null ? (
            <IconButton
              name="search"
              size="small"
              variant="tertiary"
              aria-label="Search solutions"
              test-id="bobcat-home-landing-search"
              onClick={() => setSearch('')}
            />
          ) : (
            <input
              className="c-home-landing__search"
              type="search"
              placeholder="Search solutions…"
              aria-label="Search solutions"
              value={search}
              data-testid="bobcat-home-landing-search-input"
              // Opened by a click, so focus follows it there.
              autoFocus
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setSearch(null);
              }}
              onBlur={() => {
                if (!search) setSearch(null);
              }}
            />
          ))}
      </header>
      <div className="c-home-landing">
        <button
          type="button"
          className="c-home-landing__create"
          data-testid="bobcat-home-landing-create"
          disabled={createDisabled}
          style={{ backgroundImage: `${CREATE_SHADE}, url(${createWaves})` }}
          onClick={onCreate}
        >
          <span className="c-home-landing__create-body">
            <span className="c-home-landing__create-title">
              Create an agentic solution
            </span>
          </span>
          <span className="c-home-landing__create-plus" aria-hidden="true">
            <Icon name="plus" size="small" />
          </span>
        </button>
        {shown.map((s) => (
          <SolutionCard
            key={s.id}
            solution={s}
            facts={facts.get(s.id)}
            onOpen={() => onOpen(s)}
          />
        ))}
        {needle && shown.length === 0 && (
          <p className="c-home-landing__none" role="status">
            No solution matches “{search?.trim()}”.
          </p>
        )}
      </div>
    </div>
  );
};

/** The prototype's two path marks: a conversation, and a form with a tick. */
const TalkMark = () => (
  <svg
    viewBox="0 0 48 48"
    width="44"
    height="44"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinejoin="round"
  >
    <path d="M8 12a4 4 0 0 1 4-4h18a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H18l-6 5v-5a4 4 0 0 1-4-4z" />
    <path
      d="M40 20v10a4 4 0 0 1-4 4v5l-6-5h-8"
      stroke="var(--ps-color-purple-300)"
    />
    <path d="M15 15h12M15 20h8" strokeLinecap="round" />
  </svg>
);

const StepsMark = () => (
  <svg
    viewBox="0 0 48 48"
    width="44"
    height="44"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinejoin="round"
  >
    <rect x="9" y="6" width="30" height="36" rx="4" />
    <path d="M16 16h16M16 24h16M16 32h9" strokeLinecap="round" />
    <circle
      cx="35"
      cy="35"
      r="7"
      fill="var(--ps-color-base-white)"
      stroke="var(--ps-color-purple-300)"
    />
    <path
      d="M32 35l2 2 4-4"
      stroke="var(--ps-color-purple-300)"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * One way in. The whole card starts that path; its "Preview" tip turns
 * the card into a demo of it while hovered or focused, and back once the
 * pointer leaves the card (prototype `.path-card`, `peekCard`).
 */
const PathCard: FC<{
  mark: JSX.Element;
  title: string;
  desc: string;
  peekLabel: string;
  testId: string;
  disabled?: boolean;
  demo: (playing: boolean) => JSX.Element;
  onStart: () => void;
}> = ({ mark, title, desc, peekLabel, testId, disabled, demo, onStart }) => {
  const [peeking, setPeeking] = useState(false);
  const start = () => !disabled && onStart();
  return (
    <div
      className={`c-home-start__card c-home-start__path${
        peeking ? ' is-peeking' : ''
      }${disabled ? ' is-disabled' : ''}`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={title}
      aria-disabled={disabled || undefined}
      data-testid={testId}
      onClick={start}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          start();
        }
      }}
      onMouseLeave={() => setPeeking(false)}
    >
      <span className="c-home-start__card-icon" aria-hidden="true">
        {mark}
      </span>
      <span className="c-home-start__card-title">{title}</span>
      <span className="c-home-start__card-desc">{desc}</span>
      <span className="c-home-start__card-go">
        Start
        <Icon name="arrow-right" size="xsmall" />
      </span>
      <button
        type="button"
        className="c-home-start__peek"
        aria-label={peekLabel}
        aria-pressed={peeking}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => setPeeking(true)}
        onFocus={() => setPeeking(true)}
        onBlur={() => setPeeking(false)}
      >
        <Icon name="play" size="xsmall" />
        Preview
      </button>
      <div className="c-home-start__demo">{demo(peeking)}</div>
    </div>
  );
};

/**
 * "How do you want to start?" (prototype `#startPick`): two ways to the same
 * thing — a solution with a Supervisor and a Knowledge Agent — in a
 * conversation, or step by step through the setup screens.
 */
export const StartPick: FC<{
  firstSolution: boolean;
  disabled?: boolean;
  onBack?: () => void;
  onTalk: () => void;
  onSteps: () => void;
  /** An empty canvas: no Supervisor or Knowledge Agent. */
  onBlank?: () => void;
}> = ({ firstSolution, disabled, onBack, onTalk, onSteps, onBlank }) => {
  return (
    <div className="c-home-start" data-testid="bobcat-home-start">
      {onBack && (
        <Button size="small" variant="ghost" onClick={onBack}>
          <Icon slot="prefix" name="arrow-left" size="xsmall" />
          Home
        </Button>
      )}
      <header className="c-home-start__head">
        <h1 className="c-home-start__title">
          {firstSolution
            ? 'Create your first agentic solution'
            : 'Create an agentic solution'}
        </h1>
      </header>
      <div>
        <h2 className="c-home-start__question">How do you want to start?</h2>
        <p className="c-home-start__lead">
          Both start you with a Supervisor and a Knowledge Agent.
        </p>
      </div>
      <div className="c-home-start__grid">
        <PathCard
          mark={<TalkMark />}
          title="Talk it through"
          desc="Tell us what it should do, in a line or a detailed brief. We draft the agents and ask only about what’s missing."
          peekLabel="Preview talking it through"
          testId="bobcat-home-start-talk"
          disabled={disabled}
          demo={(playing) => <TalkDemo playing={playing} />}
          onStart={onTalk}
        />
        <PathCard
          mark={<StepsMark />}
          title="Set it up step by step"
          desc="Name it, give it a tone and documents, and list the tasks it should handle, one screen at a time."
          peekLabel="Preview setting it up step by step"
          testId="bobcat-home-start-steps"
          disabled={disabled}
          demo={(playing) => <StepsDemo playing={playing} />}
          onStart={onSteps}
        />
      </div>
      {onBlank && (
        <p className="c-home-start__blank">
          Or{' '}
          <button
            type="button"
            className="c-home-start__blank-link"
            data-testid="bobcat-home-start-blank"
            disabled={disabled}
            onClick={onBlank}
          >
            start with a blank solution
          </button>{' '}
          — an empty canvas, and you build every agent yourself.
        </p>
      )}
    </div>
  );
};
